/**
* SDK pour effectuer des requêtes GraphQL vers l'API Storefront de Shopify
* Se concentre principalement sur les fonctionnalités de remises (discounts)
*/
class DeployerStorefrontApi {
    /**
     * Initialise le SDK avec une configuration
     * @param {object} config - Configuration de l'API (optionnelle)
     */
    constructor(config = {}) {
        // Configuration par défaut avec fusion des options fournies
        this.config = {
            STOREFRONT_ACCESS_TOKEN: '91e82051f832a005ba99b52ec54081ff', // Token d'accès à l'API Storefront
            SHOP_DOMAIN: window.themeVariables.routes.host, // Domaine du shop
            API_VERSION: '2025-04', // Version de l'API Storefront
            TIMEOUT: 10000, // 10 secondes
            ...config
        };
 
        // Validation des paramètres obligatoires
        if (!this.config.SHOP_DOMAIN) {
            console.warn('DeployerStorefrontApi: SHOP_DOMAIN est requis');
        }
    }
 
    /**
     * Construit l'URL de l'API Storefront
     * @private
     * @returns {string} URL complète de l'API
     */
    #getApiUrl() {
        return `https://${this.config.SHOP_DOMAIN}/api/${this.config.API_VERSION}/graphql.json`;
    }
 
    /**
     * Effectue une requête GraphQL vers l'API Storefront
     * @param {string} query - Requête GraphQL
     * @param {object} variables - Variables pour la requête GraphQL
     * @param {object} options - Options additionnelles
     * @returns {Promise} - Promesse avec la réponse
     */
    async request(query, variables = {}, options = {}) {
        const {
            timeout = this.config.TIMEOUT,
            headers = {},
        } = options;
 
        // Créer l'AbortController pour le timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
 
        try {
            // Préparer les en-têtes
            const requestHeaders = {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': this.config.STOREFRONT_ACCESS_TOKEN || '',
                ...headers,
            };

            // Préparer les options de fetch
            const fetchOptions = {
                method: 'POST',
                headers: requestHeaders,
                signal: controller.signal,
                body: JSON.stringify({
                    query,
                    variables
                })
            };
 
            // Effectuer la requête
            const response = await fetch(this.#getApiUrl(), fetchOptions);
 
            // Nettoyer le timeout
            clearTimeout(timeoutId);
 
            // Vérifier si la réponse est OK
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
 
            // Parser la réponse JSON
            const data = await response.json();
 
            // Vérifier les erreurs GraphQL
            if (data.errors) {
                throw {
                    message: 'GraphQL Error',
                    errors: data.errors,
                    status: response.status
                };
            }
 
            return data.data;
        } catch (error) {
            if (error.name === 'AbortError') {
                throw {
                    message: 'Request timeout',
                    status: 408
                };
            }
            throw error;
        }
    }

    /**
     * Applique un code de réduction au panier
     * @param {string} discountCode - Code de réduction à appliquer
     * @returns {Promise} - Promesse avec le résultat
     */
    async applyDiscountCode(discountCode) {
        const query = `
            mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
                cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                    cart {
                        id
                        discountCodes {
                            code
                            applicable
                        }
                        cost {
                            totalAmount {
                                amount
                                currencyCode
                            }
                            subtotalAmount {
                                amount
                                currencyCode
                            }
                        }
                    }
                    userErrors {
                        field
                        message
                    }
                }
            }
        `;

        // Récupérer l'ID du panier actuel
        const cartResponse = await fetch('/cart.json');
        const cartData = await cartResponse.json();
        const cartId = `gid://shopify/Cart/${cartData.token}`;

        return this.request(query, {
            cartId,
            discountCodes: [discountCode]
        });
    }

    /**
     * Supprime tous les codes de réduction du panier
     * @returns {Promise} - Promesse avec le résultat
     */
    async removeDiscountCodes() {
        const query = `
            mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
                cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                    cart {
                        id
                        discountCodes {
                            code
                            applicable
                        }
                        cost {
                            totalAmount {
                                amount
                                currencyCode
                            }
                            subtotalAmount {
                                amount
                                currencyCode
                            }
                        }
                    }
                    userErrors {
                        field
                        message
                    }
                }
            }
        `;

        // Récupérer l'ID du panier actuel
        const cartResponse = await fetch('/cart.json');
        const cartData = await cartResponse.json();
        const cartId = `gid://shopify/Cart/${cartData.token}`;

        return this.request(query, {
            cartId,
            discountCodes: [] // Tableau vide pour supprimer tous les codes
        });
    }
}

// Exporter une instance singleton du SDK
const deployerStorefrontApiInstance = new DeployerStorefrontApi();

// Exporter le SDK pour une utilisation globale
window.DeployerStorefrontApi = deployerStorefrontApiInstance; 